import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { verifieerJaarverslag } from '../services/teamJaarverslagApi';

const fmtDag = (iso: string) => new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });

/** Publieke QR-bestemming van het geconsolideerde jaarverslag (migratie 174, geen login). */
export const JaarverslagVerificatiePage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const { data, isLoading, isError } = useQuery({ queryKey: ['jaarverslag-verificatie', code], queryFn: () => verifieerJaarverslag(code), retry: false });

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-xl">
        <div className="flex items-center gap-2 text-[#0B1F3A]">
          <ShieldCheck size={22} />
          <h1 className="text-lg font-bold">Verificatie jaarverslag</h1>
        </div>

        {isLoading ? (
          <div className="mt-8 flex justify-center text-gray-400">
            <Loader2 className="animate-spin" />
          </div>
        ) : isError || !data ? (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-800">Onbekende verificatiecode</p>
              <p className="mt-1 text-xs text-red-700">Deze code hoort niet bij een door BeLagom vrijgegeven jaarverslag.</p>
            </div>
          </div>
        ) : (
          <>
            {data.geldig ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-5">
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">Echt en ongewijzigd</p>
                  <p className="mt-1 text-xs text-green-700">
                    Dit jaarverslag is zo vrijgegeven. Komen de cijfers hieronder overeen met je document, dan is het document niet aangepast.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
                <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                <div>
                  <p className="text-sm font-semibold text-red-800">Zegel klopt niet</p>
                  <p className="mt-1 text-xs text-red-700">De opgeslagen gegevens wijken af van het zegel bij vrijgave. Neem contact op met BeLagom.</p>
                </div>
              </div>
            )}

            <dl className="mt-4 rounded-xl border border-gray-100 bg-white p-5 shadow-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-gray-500">Opdrachtgever</dt>
              <dd className="text-gray-900">{data.opdrachtgever}</dd>
              <dt className="text-gray-500">Jaarverslag</dt>
              <dd className="text-gray-900">{data.jaar} · {data.discipline}</dd>
              <dt className="text-gray-500">Vrijgegeven op</dt>
              <dd className="text-gray-900">{fmtDag(data.vrijgegeven_op)}</dd>
              <dt className="text-gray-500">Opgesteld door</dt>
              <dd className="text-gray-900">Team van {data.aantal_vertrouwenspersonen} professionals</dd>
              <dt className="text-gray-500">Casussen</dt>
              <dd className="text-gray-900">{data.aantal_casussen ?? 'onder de privacygrens'}</dd>
              <dt className="text-gray-500">Gesprekken</dt>
              <dd className="text-gray-900">{data.aantal_gesprekken ?? '—'}</dd>
              <dt className="text-gray-500">SHA-256</dt>
              <dd className="font-mono text-xs break-all text-gray-700">{data.zegel}</dd>
            </dl>
          </>
        )}
      </div>
    </div>
  );
};

export default JaarverslagVerificatiePage;
